import { LitElement, html, css } from 'lit';
import { unsafeSVG } from 'lit/directives/unsafe-svg.js';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Inbox, Settings, Check, GripVertical } from 'lucide';

import { lucideIcon } from '../icon.js';

export class MvTable extends LitElement {
  static properties = {
    columns: { type: Array },
    data: { type: Array },
    emptyText: { type: String },
    currentPage: { type: Number },
    /**
     * Rijen verslepen. Aan = elke rij krijgt een greep vooraan; slepen begint alleen vanaf die
     * greep, zodat klikken, selecteren en uitklappen blijven werken zoals ze deden.
     *
     * De tabel bepaalt niet wat een verplaatsing betekent — dat weet alleen de pagina. Bij het
     * loslaten volgt `row-drop` met het gesleepte id, het id waarop losgelaten is en of het
     * ervoor of erna hoort.
     */
    rowDrag: { type: Boolean },
    _dragId: { state: true },     // id dat nu gesleept wordt (of null)
    _overId: { state: true },     // rij waar de cursor boven hangt
    _overBefore: { state: true }, // erboven (true) of eronder (false) invoegen
    /*
     * Greep ingedrukt. Moet reactief zijn: het `draggable`-attribuut hangt ervan af, en zonder
     * hertekenen blijft dat op false staan — dan begint de sleep nooit. Lit werkt bij in een
     * microtask, dus ruim vóór de muisbeweging die het slepen daadwerkelijk start.
     */
    _dragArmed: { state: true },
    paginate: { type: Boolean },   // Aan = toon één pagina tegelijk met een stapper eronder
    pageSize: { type: Number },
    totalItems: { type: Number },  // For server-side pagination: total rows in backend
    serverSide: { type: Boolean }, // Set true to use server-side pagination
    expandable: { type: Boolean },
    renderDetails: { type: Object },
    _sortCol: { state: true },
    _sortDir: { state: true },
    // Kolomkiezer: welke EXTRA kolommen bestaan, en welke daarvan aan staan. De tabel
    // bewaart de keuze niet zelf -- de eigenaar doet dat, want die weet waar hij hoort
    // (bij deze gebruiker, op de server).
    columnOptions: { type: Array },   // [{ id, label }]
    visibleColumns: { type: Array },  // ids die aan staan
    _pickerOpen: { state: true },
    _pickerPos: { state: true },   // viewport-positie van het menu
  };

  static styles = css`
    /* Snel bewerken in een cel (class 'ie' in de render van een kolom, zie project-detail). */
    .ie { white-space:nowrap; cursor:pointer; border-radius:5px; padding:2px 4px; margin:-2px -4px; display:inline-flex; align-items:center; gap:4px; max-width:100%; }
    .ie:hover { background:var(--mv-surface-alt); box-shadow:0 0 0 1px var(--mv-border); }
    .ie-edit { display:inline-flex; align-items:center; gap:6px; max-width:100%; }
    .ie-input { font:inherit; font-size:0.82rem; font-weight:600; color:var(--mv-text); background:var(--mv-surface); border:1.5px solid var(--mv-primary);
      border-radius:6px; padding:4px 7px; min-width:0; max-width:100%; box-shadow:0 0 0 3px var(--mv-primary-tint); }
    .ie-input:focus { outline:none; }
    input.ie-input[type="text"] { width:120px; }
    .ie-chip { font:inherit; font-size:0.72rem; font-weight:700; padding:3px 9px; border-radius:6px; cursor:pointer;
      border:1px solid var(--mv-border); background:var(--mv-surface); color:var(--mv-text); }
    .ie-chip.on { background:#c2410c; border-color:#c2410c; color:#fff; }
    .ie-done { font:inherit; font-size:0.72rem; font-weight:700; border:none; background:none; color:var(--mv-primary); cursor:pointer; padding:3px 4px; }

    /* Kolomkiezer — tandwiel rechtsboven in de koprij. */
    /* Rechterrand gelijk aan de celinhoud (th en td hebben allebei 1.5rem padding), zodat
       het tandwiel exact boven het prullenbak-icoon van een rij staat. */
    /* GEEN transform hier: een getransformeerd element wordt de containing block voor
       position:fixed-kinderen, waardoor het menu alsnog binnen de tabel valt en wordt
       afgeknipt. Verticaal centreren gaat daarom met flex over de volle celhoogte. */
    .colpick { position: absolute; right: 1.5rem; top: 0; bottom: 0; display: flex; align-items: center; }
    /* De koptekst van de laatste kolom mag nooit ONDER het tandwiel doorlopen. Het tandwiel
       staat buiten de tekststroom, dus die ruimte moet de cel zelf reserveren: de knop plus
       lucht bovenop de gewone celpadding. Bij drie kolommen valt dat niet op -- pas als
       iemand kolommen bijzet komt de laatste kop tegen het tandwiel aan. */
    th.has-colpick { padding-right: calc(1.5rem + var(--colpick-w, 22px) + 10px); }
    .colpick-btn { background: none; border: none; cursor: pointer; padding: 4px; border-radius: 5px;
      color: var(--mv-text-muted, #9aa0a6); display: inline-flex; align-items: center; }
    .colpick-btn:hover { color: var(--mv-text, #37474f); background: var(--mv-surface-alt, #f3f4f6); }
    .colpick-menu { position: fixed; z-index: var(--mv-z-overlay); min-width: 210px;
      background: var(--mv-surface, #fff); border: 1px solid var(--mv-border, #e5e7eb); border-radius: .6rem;
      box-shadow: 0 10px 28px rgba(0,0,0,.12); padding: 6px; text-align: left; }
    .colpick-head { font-size: .68rem; font-weight: 700; text-transform: uppercase; letter-spacing: .05em;
      color: var(--mv-text-muted, #9aa0a6); padding: 6px 8px 8px; }
    .colpick-item { display: flex; align-items: center; gap: 9px; width: 100%; background: none; border: none;
      cursor: pointer; padding: 7px 8px; border-radius: .4rem; font: inherit; font-size: .82rem; font-weight: 500;
      text-transform: none; letter-spacing: normal; color: var(--mv-text, #37474f); }
    .colpick-item:hover { background: var(--mv-surface-alt, #f3f4f6); }
    .colpick-box { width: 15px; height: 15px; border-radius: 4px; border: 1.5px solid var(--mv-border-strong, #cfd8dc);
      display: inline-flex; align-items: center; justify-content: center; flex: 0 0 auto; color: #fff; }
    .colpick-box.on { background: var(--mv-primary, #4b9d35); border-color: var(--mv-primary, #4b9d35); }

    :host {
      display: block;
      width: 100%;
      background: var(--mv-surface, #FFFFFF);
      border-radius: 0.75rem;
      box-shadow: var(--mv-shadow, 0px 8px 24px 0px rgba(82, 63, 105, 0.06));
      border: 1px solid var(--mv-border, #EFF2F5);
      overflow: hidden;
    }

    /* Table responsive wrapper */
    .table-responsive {
      width: 100%;
      overflow-x: auto;
    }

    table {
      width: 100%;
      border-collapse: collapse;
      text-align: left;
      min-width: 400px;
    }

    thead {
      background: var(--mv-surface-alt, #F9FAFB);
    }

    th {
      font-weight: 700;
      color: var(--mv-text-muted, #A1A5B7);
      font-size: 0.75rem;
      text-transform: uppercase;
      letter-spacing: 0.06rem;
      padding: 0.9rem 1.5rem;
      border-bottom: 1px solid var(--mv-border, #EFF2F5);
      white-space: nowrap;
      user-select: none;
    }

    th.sortable {
      cursor: pointer;
    }

    th.sortable:hover {
      color: var(--mv-text-sub, #3F4254);
    }

    .sort-ind {
      display: inline-block;
      margin-left: 4px;
      font-size: 0.7rem;
      opacity: 0.55;
    }

    tbody tr.data-row {
      border-bottom: 1px solid var(--mv-border, #EFF2F5);
      cursor: pointer;
      transition: background 0.15s ease;
    }

    tbody tr.data-row:hover {
      background: var(--mv-surface-alt, #F9FAFB);
    }

    tbody tr.data-row.expanded {
      background: var(--mv-bg, #F5F8FA);
      border-bottom: none;
    }

    tbody tr.data-row:last-of-type:not(.expanded) {
      border-bottom: none;
    }

    td {
      padding: 1rem 1.5rem;
      vertical-align: middle;
      color: var(--mv-text-sub, #3F4254);
      font-weight: 500;
      font-size: 0.9rem;
    }

    /* Expand indicator column */
    .expand-td {
      width: 40px;
      padding: 1rem 0.5rem 1rem 1.25rem;
    }

    .expand-indicator {
      width: 24px;
      height: 24px;
      border-radius: 6px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: var(--mv-text-muted, #B5B5C3);
      transition: all 0.2s ease;
      background: transparent;
    }

    .expanded .expand-indicator {
      color: var(--mv-primary, #10B981);
      background: var(--mv-primary-tint, #E8FFF3);
      transform: rotate(90deg);
    }

    /*
     * Expanded details row.
     *
     * De uitgeklapte cel loopt over alle kolommen (colspan), en een browser rekent de
     * breedte van zo'n cel MEE bij het verdelen van de kolombreedtes. Het brede boorprofiel
     * daarin duwde daardoor Naam en Profiel uit elkaar, waarna de Details-chips op twee
     * regels vielen — de tabel verspringt dan zodra je een rij opent.
     *
     * width:1px maakt de gewenste breedte van de inhoud vrijwel nul, zodat hij niets aan de
     * kolomverdeling bijdraagt; min-width:100% blaast hem daarna weer op tot de volle
     * celbreedte. De kolommen blijven zo staan waar ze stonden.
     */
    .details-row td {
      padding: 0;
      border-bottom: 1px solid var(--mv-border, #EFF2F5);
      background: var(--mv-surface, #ffffff);
    }

    .details-inner {
      width: 1px;
      min-width: 100%;
      box-sizing: border-box;
      padding: 1.5rem;
      border-top: 2px solid var(--mv-border, #EFF2F5);
      animation: slideDown 0.2s ease-out;
    }

    @keyframes slideDown {
      from { opacity: 0; transform: translateY(-8px); }
      to   { opacity: 1; transform: translateY(0); }
    }

    /* Footer */
    .table-footer {
      padding: 0.85rem 1.5rem;
      border-top: 1px solid var(--mv-border, #EFF2F5);
      display: flex;
      justify-content: space-between;
      align-items: center;
      background: var(--mv-surface-alt, #FAFAFA);
      flex-wrap: wrap;
      gap: 0.75rem;
    }

    .grip-th { width: 26px; }
    .grip-td { width: 26px; padding-right: 0; }
    .grip {
      display: inline-flex; align-items: center; justify-content: center;
      color: var(--mv-text-muted, #a1a5b7); cursor: grab; padding: 2px;
    }
    .grip:active { cursor: grabbing; }
    /* De rij die je optilt blijft zichtbaar maar treedt terug. */
    tr.data-row.dragging { opacity: .45; }
    /* Waar hij terechtkomt: een lijn aan de kant waar ingevoegd wordt. */
    tr.data-row.drop-before td { box-shadow: inset 0 2px 0 0 var(--mv-primary, #4b9d35); }
    tr.data-row.drop-after td { box-shadow: inset 0 -2px 0 0 var(--mv-primary, #4b9d35); }

    .pagination-info {
      font-size: 0.82rem;
      color: var(--mv-text-muted, #A1A5B7);
      font-weight: 500;
    }

    .pagination-info strong {
      color: var(--mv-text, #3F4254);
    }

    .pagination-controls {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 0.35rem;
    }

    .ellipsis {
      color: var(--mv-text-muted, #A1A5B7);
      padding: 0 4px;
      font-size: 0.85rem;
    }

    .page-btn {
      /* Minimaal vierkant voor een paginanummer, maar breder als de tekst dat vraagt: met een
         vaste breedte liep 'Volgende' over de knop ernaast en viel hij buiten de kaart. */
      min-width: 32px;
      height: 32px;
      border: 1px solid var(--mv-border, #E4E6EF);
      border-radius: 0.35rem;
      background: var(--mv-surface, #fff);
      color: var(--mv-text-sub, #7E8299);
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: all 0.15s;
      font-size: 0.85rem;
      font-weight: 600;
      padding: 0 6px;
      white-space: nowrap;
    }

    .page-btn.tekst { padding: 0 12px; }

    .page-btn:hover:not(:disabled) {
      background: var(--mv-primary, #10B981);
      border-color: var(--mv-primary, #10B981);
      color: #fff;
    }

    .page-btn:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }

    .page-btn.active {
      background: var(--mv-primary, #10B981);
      border-color: var(--mv-primary, #10B981);
      color: #fff;
    }

    .page-size-select {
      height: 32px;
      padding: 0 0.6rem;
      border: 1px solid var(--mv-border, #E4E6EF);
      border-radius: 0.35rem;
      background: var(--mv-surface, #fff);
      color: var(--mv-text, #3F4254);
      font-size: 0.82rem;
      font-weight: 600;
      font-family: inherit;
      cursor: pointer;
      outline: none;
    }

    .page-size-select:focus {
      border-color: var(--mv-primary, #10B981);
    }

    /* Empty state */
    .empty-state {
      padding: 4rem 2rem;
      text-align: center;
      color: var(--mv-text-muted, #B5B5C3);
    }

    .empty-icon {
      margin-bottom: 1rem;
      opacity: 0.4;
    }

    .empty-state p {
      font-size: 0.95rem;
      font-weight: 500;
    }

    /* Injected styles for mapped custom HTML rows */
    .action-row {
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }
    .action-btn {
      background: transparent;
      border: none;
      color: var(--mv-text-muted, #A1A5B7);
      cursor: pointer;
      padding: 0.4rem;
      border-radius: 0.35rem;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      transition: all 0.2s ease;
    }
    .action-btn:hover {
      background: var(--mv-surface-alt, #F9FAFB);
      color: var(--mv-primary, #10B981);
    }
    .action-btn.delete:hover {
      background: var(--mv-danger-bg, #FFF5F8);
      color: var(--mv-danger, #F1416C);
    }
    .action-disabled {
      opacity: 0.4;
      pointer-events: none;
      cursor: not-allowed;
    }

    .status-pill {
      font-size: 0.75rem;
      font-weight: 600;
      padding: 0.25rem 0.5rem;
      border-radius: 4px;
      display: inline-block;
    }
    .status-active { background: var(--mv-primary-tint, #E8FFF3); color: var(--mv-primary, #10B981); }
    .status-inactive { background: var(--mv-danger-bg, #FFF5F8); color: var(--mv-danger, #F1416C); }

    .role-badge {
      display: inline-block;
      padding: 0.25rem 0.6rem;
      border-radius: 4px;
      font-size: 0.75rem;
      font-weight: 600;
    }
    .role-badge.admin { background: #F1EAFF; color: #7239EA; }
    .role-badge.user { background: var(--mv-surface-alt, #F9FAFB); color: var(--mv-text-sub, #5E6278); }

    /* Responsive column hiding */
    @media (max-width: 768px) {
      .col-hide-mobile {
        display: none;
      }
      .status-badge-label {
        display: none;
      }
    }
  `;

  constructor() {
    super();
    this.columns = [];
    this.data = [];
    this.emptyText = 'Geen gegevens gevonden.';
    this.currentPage = 1;
    // Paginering staat standaard UIT. Zo bleef hij jarenlang: de meeste tabellen hier zijn kort
    // genoeg om in één keer te tonen, en een stapper onder acht rijen is bediening zonder doel.
    // Wie hem nodig heeft zet 'm aan met het attribuut `paginate`.
    this.rowDrag = false;
    this._dragId = null;
    this._overId = null;
    this._overBefore = true;
    this._dragArmed = false;   // greep ingedrukt → deze rij mag slepen
    this.paginate = false;
    this.pageSize = 10;
    this.totalItems = null; // null = use data.length (client-side)
    this.serverSide = false;
    this.expandable = false;
    this.renderDetails = null;
    this._sortCol = null;
    this._sortDir = 'asc';
  }

  /** A column is interactively sortable only when it provides a sortValue() accessor
   *  that returns a primitive — this keeps columns whose cell value is HTML safe. */
  /**
   * Tandwiel + uitklaplijst om extra kolommen aan of uit te zetten.
   *
   * De tabel houdt alleen bij of het menu open staat; welke kolommen aan staan en waar dat
   * wordt bewaard is aan de eigenaar. Zo blijft dit component onwetend van gebruikers,
   * projecten en opslag.
   */
  connectedCallback() {
    super.connectedCallback();
    // Buiten het menu klikken sluit het. Op document-niveau, want een klik elders in de
    // pagina bereikt deze component nooit.
    this._closePicker = () => { if (this._pickerOpen) this._pickerOpen = false; };
    document.addEventListener('click', this._closePicker);
    // Bij scrollen loopt het vaste menu weg van de knop; dan liever dicht dan scheef.
    // Capture, zodat ook scrollen binnen de tabel meetelt.
    document.addEventListener('scroll', this._closePicker, true);
    window.addEventListener('resize', this._closePicker);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this._closePicker);
    document.removeEventListener('scroll', this._closePicker, true);
    window.removeEventListener('resize', this._closePicker);
    super.disconnectedCallback();
  }

  _renderColumnPicker() {
    const opts = this.columnOptions || [];
    if (!opts.length) return '';
    const on = new Set(this.visibleColumns || []);
    return html`
      <div class="colpick" @click=${(e) => e.stopPropagation()}>
        <button class="colpick-btn" title="Kolommen kiezen" aria-haspopup="true" aria-expanded=${this._pickerOpen ? 'true' : 'false'}
          @click=${(e) => this._togglePicker(e)}>
          ${unsafeSVG(lucideIcon(Settings, 14))}
        </button>
        ${this._pickerOpen ? html`
          <div class="colpick-menu" style="top:${this._pickerPos?.top ?? 0}px; right:${this._pickerPos?.right ?? 0}px;">
            <div class="colpick-head">Kolommen tonen</div>
            ${opts.map((o) => {
              const checked = on.has(o.id);
              return html`
                <button class="colpick-item" role="menuitemcheckbox" aria-checked=${checked ? 'true' : 'false'}
                  @click=${() => this._toggleColumn(o.id)}>
                  <span class="colpick-box ${checked ? 'on' : ''}">${checked ? unsafeSVG(lucideIcon(Check, 12)) : ''}</span>
                  <span>${o.label}</span>
                </button>`;
            })}
          </div>` : ''}
      </div>`;
  }

  /**
   * Open het menu op vaste viewport-positie.
   *
   * De tabel zit in twee clipping-contexten -- de kaart knipt af voor haar ronde hoeken, de
   * responsive wrapper voor horizontaal scrollen. Een menu binnen die stroom wordt dus
   * afgesneden. Met `position: fixed` en de coördinaten van de knop staat het erbuiten.
   */
  _togglePicker(e) {
    if (this._pickerOpen) { this._pickerOpen = false; return; }
    const r = e.currentTarget.getBoundingClientRect();
    this._pickerPos = { top: Math.round(r.bottom + 6), right: Math.round(window.innerWidth - r.right) };
    this._pickerOpen = true;
  }

  _toggleColumn(id) {
    const on = new Set(this.visibleColumns || []);
    on.has(id) ? on.delete(id) : on.add(id);
    // Volgorde van columnOptions aanhouden, zodat de kolommen niet verspringen op
    // aanklikvolgorde.
    const next = (this.columnOptions || []).map((o) => o.id).filter((x) => on.has(x));
    this.dispatchEvent(new CustomEvent('columns-changed', { detail: { visible: next }, bubbles: true, composed: true }));
  }

  _isSortable(col) {
    return typeof col?.sortValue === 'function';
  }

  _toggleSort(col) {
    if (!this._isSortable(col)) return;
    if (this._sortCol === col.id) {
      this._sortDir = this._sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this._sortCol = col.id;
      this._sortDir = col.defaultSortDir === 'desc' ? 'desc' : 'asc';
    }
  }

  get _sortedData() {
    const col = (this.columns || []).find(c => c.id === this._sortCol);
    if (!this._sortCol || !this._isSortable(col)) return this.data;
    const dir = this._sortDir === 'asc' ? 1 : -1;
    return [...(this.data || [])].sort((a, b) => {
      const va = col.sortValue(a);
      const vb = col.sortValue(b);
      const ea = va === null || va === undefined || va === '';
      const eb = vb === null || vb === undefined || vb === '';
      if (ea && eb) return 0;
      if (ea) return 1;   // empties always sort last, regardless of direction
      if (eb) return -1;
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir;
      return String(va).localeCompare(String(vb), undefined, { numeric: true }) * dir;
    });
  }

  get effectiveTotal() {
    // If totalItems is provided (server-side), use that; otherwise use data length
    return this.serverSide && this.totalItems !== null ? this.totalItems : this.data.length;
  }

  updated(changed) {
    super.updated?.(changed);
    // Een filter kan de lijst korter maken dan de pagina waar je op staat. Zonder dit blijf je
    // naar een lege tabel kijken met een stapper die zegt dat je op pagina 7 van 2 staat.
    if (this.paginate && !this.serverSide && this.currentPage > this.totalPages) {
      this.currentPage = this.totalPages;
    }
  }

  get paginatedData() {
    // Zonder `paginate` alles tonen — dat is hoe elke bestaande tabel zich gedraagt.
    if (!this.paginate) return this._sortedData;
    // Bij server-side paginering levert de aanroeper al precies één pagina aan; nog eens
    // snijden zou de tweede helft van die pagina opeten.
    if (this.serverSide) return this._sortedData;
    const start = (this.currentPage - 1) * this.pageSize;
    return this._sortedData.slice(start, start + this.pageSize);
  }

  get totalPages() {
    if (this.pageSize <= 0 || this.effectiveTotal === 0) return 1;
    return Math.ceil(this.effectiveTotal / this.pageSize);
  }

  _emitPageChange(page, pageSize) {
    this.dispatchEvent(new CustomEvent('page-change', {
      detail: { page, pageSize },
      bubbles: true,
      composed: true
    }));
  }

  goToPage(page) {
    if (page < 1 || page > this.totalPages) return;
    if (this.serverSide) {
      this._emitPageChange(page, this.pageSize);
    } else {
      this.currentPage = page;
    }
  }

  handlePageSizeChange(e) {
    const newSize = parseInt(e.target.value, 10);
    if (this.serverSide) {
      this._emitPageChange(1, newSize);
    } else {
      this.pageSize = newSize;
      this.currentPage = 1;
    }
  }

  // ── Rijen verslepen ────────────────────────────────────────────────────────
  // De tabel weet niet wat een verplaatsing betekent: of het een andere volgorde is of een
  // andere categorie hangt af van waar hij staat. Hij meldt alleen wát er waar is losgelaten.

  /**
   * Greep ingedrukt: deze rij mag nu gesleept worden.
   *
   * Het ontwapenen hangt aan het DOCUMENT en niet aan de greep. Laat je los buiten de greep --
   * en dat doe je altijd, want je hebt net gesleept -- dan zou een mouseup op de greep zelf
   * nooit komen en bleef de rij sleepbaar zonder dat je de greep nog aanraakte.
   */
  _armDrag() {
    this._dragArmed = true;
    const off = () => { this._dragArmed = false; document.removeEventListener('mouseup', off); };
    document.addEventListener('mouseup', off);
  }

  _onDragStart(e, row) {
    if (!this.rowDrag || !this._dragArmed) { e.preventDefault(); return; }
    this._dragId = row.id;
    // text/plain, want dat overleeft de grens tussen twee tabellen (en twee shadow roots).
    e.dataTransfer.setData('text/plain', String(row.id));
    e.dataTransfer.effectAllowed = 'move';
  }

  _onDragEnd() {
    this._dragArmed = false;
    this._dragId = null;
    this._overId = null;
  }

  _onDragOver(e, row) {
    if (!this.rowDrag) return;
    e.preventDefault();                 // zonder dit weigert de browser de drop
    e.dataTransfer.dropEffect = 'move';
    // Boven- of onderhelft bepaalt of je ervoor of erna invoegt.
    const r = e.currentTarget.getBoundingClientRect();
    this._overBefore = (e.clientY - r.top) < r.height / 2;
    this._overId = row.id;
  }

  _onDrop(e, row) {
    if (!this.rowDrag) return;
    e.preventDefault();
    e.stopPropagation();
    const draggedId = e.dataTransfer.getData('text/plain');
    const before = this._overBefore;
    this._overId = null;
    this._dragId = null;
    this._dragArmed = false;
    if (!draggedId || draggedId === String(row.id)) return;
    this.dispatchEvent(new CustomEvent('row-drop', {
      detail: { draggedId, targetId: String(row.id), before },
      bubbles: true, composed: true,
    }));
  }

  handleRowClick(row) {
    if (this.expandable) {
      row.expanded = !row.expanded;
      this.requestUpdate();
    }
    this.dispatchEvent(new CustomEvent('row-click', {
      detail: { row },
      bubbles: true,
      composed: true
    }));
  }

  _renderPageButtons() {
    const total = this.totalPages;
    const cur = this.currentPage;
    const pages = [];

    // Always show first, last, current ±1
    const shown = new Set();
    [1, cur - 1, cur, cur + 1, total].forEach(p => {
      if (p >= 1 && p <= total) shown.add(p);
    });

    const sorted = [...shown].sort((a, b) => a - b);
    let prev = 0;
    for (const p of sorted) {
      if (p - prev > 1) {
        // Gap - show ellipsis
        pages.push(html`<span class="ellipsis">…</span>`);
      }
      pages.push(html`
        <button class="page-btn ${p === cur ? 'active' : ''}" @click=${() => this.goToPage(p)}>${p}</button>
      `);
      prev = p;
    }
    return pages;
  }

  render() {
    const paginated = this.paginatedData;
    const total = this.effectiveTotal;
    const startIdx = total === 0 ? 0 : (this.currentPage - 1) * this.pageSize + 1;
    const endIdx = Math.min(this.currentPage * this.pageSize, total);

    return html`
      <div class="table-responsive">
        ${total === 0 && !this.serverSide ? html`
          <div class="empty-state">
            <div class="empty-icon">${unsafeSVG(lucideIcon(Inbox, 48))}</div>
            <p>${this.emptyText}</p>
          </div>
        ` : html`
          <table>
            <thead>
              <tr>
                ${this.rowDrag ? html`<th class="grip-th"></th>` : ''}
                ${this.expandable ? html`<th style="width:40px;"></th>` : ''}
                ${this.columns.map((col, i) => {
                  const sortable = this._isSortable(col);
                  const active = sortable && this._sortCol === col.id;
                  // Het tandwiel hoort rechtsboven in de koprij; die zit in de laatste kop.
                  const isLast = i === this.columns.length - 1;
                  const withPicker = isLast && !!this.columnOptions?.length;
                  return html`<th
                    class="${col.hideOnMobile ? 'col-hide-mobile' : ''} ${sortable ? 'sortable' : ''} ${withPicker ? 'has-colpick' : ''}"
                    style="width:${col.width || 'auto'}; ${withPicker ? 'position:relative;' : ''}"
                    @click=${sortable ? () => this._toggleSort(col) : null}>
                    ${col.title}${sortable ? html`<span class="sort-ind">${active ? (this._sortDir === 'asc' ? '▲' : '▼') : '↕'}</span>` : ''}
                    ${isLast ? this._renderColumnPicker() : ''}
                  </th>`;
                })}
              </tr>
            </thead>
            <tbody>
              ${paginated.map(row => html`
                <tr class="data-row ${row.expanded ? 'expanded' : ''}${this._dragId === row.id ? ' dragging' : ''}${this._overId === row.id ? (this._overBefore ? ' drop-before' : ' drop-after') : ''}"
                  draggable=${this.rowDrag && this._dragArmed ? 'true' : 'false'}
                  @click=${() => this.handleRowClick(row)}
                  @dragstart=${(e) => this._onDragStart(e, row)}
                  @dragend=${() => this._onDragEnd()}
                  @dragover=${(e) => this._onDragOver(e, row)}
                  @dragleave=${() => { if (this._overId === row.id) this._overId = null; }}
                  @drop=${(e) => this._onDrop(e, row)}>
                  ${this.rowDrag ? html`
                    <td class="grip-td" @click=${(e) => e.stopPropagation()}>
                      <span class="grip" title="Sleep om te verplaatsen"
                        @mousedown=${() => this._armDrag()}>
                        ${unsafeSVG(lucideIcon(GripVertical, 15))}
                      </span>
                    </td>
                  ` : ''}
                  ${this.expandable ? html`
                    <td class="expand-td">
                      <div class="expand-indicator">${unsafeSVG(lucideIcon(ChevronRight, 16))}</div>
                    </td>
                  ` : ''}
                  ${this.columns.map(col => html`
                    <td class="${col.hideOnMobile ? 'col-hide-mobile' : ''}" style="width:${col.width || 'auto'}">${col.render ? col.render(row) : row[col.id]}</td>
                  `)}
                </tr>
                ${this.expandable && row.expanded && this.renderDetails ? html`
                  <tr class="details-row">
                    <td colspan="${this.columns.length + (this.expandable ? 1 : 0) + (this.rowDrag ? 1 : 0)}">
                      <div class="details-inner">
                        ${this.renderDetails(row)}
                      </div>
                    </td>
                  </tr>
                ` : ''}
              `)}
            </tbody>
          </table>
        `}
      </div>

      ${this.paginate && total > 0 ? html`
        <div style="display:flex; align-items:center; justify-content:space-between; gap:12px; flex-wrap:wrap; padding:14px 20px 16px;">
          <div class="pagination-info">
            <strong>${startIdx}–${endIdx}</strong> van <strong>${total}</strong>
          </div>
          ${this.totalPages > 1 ? html`
            <div class="pagination-controls">
              <button class="page-btn tekst" ?disabled=${this.currentPage <= 1}
                @click=${() => this.goToPage(this.currentPage - 1)}>Vorige</button>
              ${this._renderPageButtons()}
              <button class="page-btn tekst" ?disabled=${this.currentPage >= this.totalPages}
                @click=${() => this.goToPage(this.currentPage + 1)}>Volgende</button>
            </div>
          ` : ''}
        </div>
      ` : ''}
    `;
  }
}

customElements.define('mv-table', MvTable);
